import React, { forwardRef, FC } from "react";
import { ResponsiveValue } from "styled-system";
import Grid, { GridProps } from "./Grid";
import { BoxProps } from "../box/Box";

interface ISimpleGrid {
  /**
   * The width at which child elements will break into columns. Pass a number for pixel values or a string for any other valid CSS length.
   */
  minChildWidth?: ResponsiveValue<React.CSSProperties["minWidth"]>;
  /**
   * The number of columns
   */
  columns?: ResponsiveValue<number>;
  /**
   * The gap between the grid items
   */
  spacing?: GridProps["gridGap"];
  /**
   * The column gap between the grid items
   */
  spacingX?: GridProps["gridGap"];
  /**
   * The row gap between the grid items
   */
  spacingY?: GridProps["gridGap"];
}

export type SimpleGridProps = BoxProps & ISimpleGrid;

const SimpleGrid: FC<SimpleGridProps> = forwardRef(
  ({ columns, spacingX, spacingY, spacing, minChildWidth, ...props }, ref) => {
    const templateColumns = minChildWidth ? widthToColumns(minChildWidth) : countToColumns(columns);

    return (
      <Grid
        ref={ref}
        gap={spacing}
        columnGap={spacingX}
        rowGap={spacingY}
        templateColumns={templateColumns}
        {...props}
      />
    );
  },
);

SimpleGrid.displayName = "SimpleGrid";

export default SimpleGrid;

const px = (n: string | number) => (typeof n === "number" ? `${n}px` : n);

export const widthToColumns = (width: any): any => {
  if (Array.isArray(width)) {
    return width.map(widthToColumns);
  }

  if (width !== null && typeof width === "object" && Object.keys(width).length > 0) {
    const acc = {};

    // eslint-disable-next-line
    for (const key in width) {
      // @ts-ignore
      acc[key] = `repeat(auto-fit, minmax(${px(width[key])}, 1fr))`;
    }
    return acc;
  }

  if (width != null) {
    return `repeat(auto-fit, minmax(${px(width)}, 1fr))`;
  }

  return null;
};

export const countToColumns = (count: any): any => {
  if (Array.isArray(count)) {
    return count.map(countToColumns);
  }

  if (count !== null && typeof count === "object" && Object.keys(count).length > 0) {
    const acc = {};

    // eslint-disable-next-line
    for (const key in count) {
      // @ts-ignore
      acc[key] = `repeat(${count[key]}, minmax(0, 1fr))`;
    }
    return acc;
  }

  if (count != null) {
    return `repeat(${count}, minmax(0, 1fr))`;
  }

  return null;
};
